import { useState, useEffect, useRef } from 'react';
import './ConnectModal.css';
import { addContactRequest } from "../../../api/ContactAPI";

const STEPS = {
    DETAILS: 1,
    CONFIRMATION: 2
};

const ConnectModal = ({ person, service, onClose }) => {
    const [currentStep, setCurrentStep] = useState(STEPS.DETAILS);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const firstInputRef = useRef(null);
    const [formData, setFormData] = useState({
        firstName: '',
        surname: '',
        email: '',
        mobile: '',
        message: ''
    });
    const [formErrors, setFormErrors] = useState({});

    // Focus first input on mount
    useEffect(() => {
        if (firstInputRef.current) {
            firstInputRef.current.focus();
        }
    }, []);

    // Handle ESC key to close
    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, [onClose]);

    // Prevent background scrolling
    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        // Clear error when user starts typing
        if (formErrors[name]) {
            setFormErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.firstName.trim()) errors.firstName = 'Required';
        if (!formData.surname.trim()) errors.surname = 'Required';
        if (!formData.email.trim()) {
            errors.email = 'Required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            errors.email = 'Invalid email';
        }
        if (!formData.mobile.trim()) errors.mobile = 'Required';

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        setIsSubmitting(true);
        const requestProps = {
            fName: formData.firstName,
            lName: formData.surname,
            email: formData.email,
            mobile: formData.mobile,
            request: formData.message,
            service: service.name+" - "+service.price,
            forNewroConnect: true,
            sendPush: true,
            trainerId: person?.id
        };
        // Call your API function here
        const res = await addContactRequest(requestProps);
        if (res.success) {
            setCurrentStep(STEPS.CONFIRMATION);

        } else {
            console.error('Request error:', res);
        }

        setIsSubmitting(false);
    };

    const renderProgressIndicator = () => (
        <div className="connect-modal__progress">
            <div className="connect-modal__step-indicator">
                <div className={`connect-modal__step-dot ${currentStep >= STEPS.DETAILS ? 'connect-modal__step-dot--completed' : ''} ${currentStep === STEPS.DETAILS ? 'connect-modal__step-dot--active' : ''}`} />
                <div className={`connect-modal__step-line ${currentStep > STEPS.DETAILS ? 'connect-modal__step-line--completed' : ''}`} />
                <div className={`connect-modal__step-dot ${currentStep >= STEPS.CONFIRMATION ? 'connect-modal__step-dot--completed' : ''} ${currentStep === STEPS.CONFIRMATION ? 'connect-modal__step-dot--active' : ''}`} />
            </div>
        </div>
    );

    const renderDetailsStep = () => (
        <div className="connect-modal__content" key="details-step">
            <div className="connect-modal__header">
                <h2 className="connect-modal__title">{service.name}</h2>
                <p className="connect-modal__subtitle">Share your details and {person?.name?.split(' ')[0]} will be in contact ASAP</p>
            </div>

            <form className="connect-form" onSubmit={e => e.preventDefault()}>
                <div className="connect-form__row">
                    <div className="connect-form__group">
                        <label className="connect-form__label">First Name</label>
                        <input
                            ref={firstInputRef}
                            type="text"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleInputChange}
                            className={`connect-form__input ${formErrors.firstName ? 'connect-form__input--error' : ''}`}
                            placeholder="John"
                        />
                        {formErrors.firstName && <span className="connect-form__error">{formErrors.firstName}</span>}
                    </div>
                    <div className="connect-form__group">
                        <label className="connect-form__label">Surname</label>
                        <input
                            type="text"
                            name="surname"
                            value={formData.surname}
                            onChange={handleInputChange}
                            className={`connect-form__input ${formErrors.surname ? 'connect-form__input--error' : ''}`}
                            placeholder="Jameson"
                        />
                        {formErrors.surname && <span className="connect-form__error">{formErrors.surname}</span>}
                    </div>
                </div>

                <div className="connect-form__group">
                    <label className="connect-form__label">Email</label>
                    <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className={`connect-form__input ${formErrors.email ? 'connect-form__input--error' : ''}`}
                        placeholder="john@example.com"
                    />
                    {formErrors.email && <span className="connect-form__error">{formErrors.email}</span>}
                </div>

                <div className="connect-form__group">
                    <label className="connect-form__label">Mobile Number</label>
                    <input
                        type="tel"
                        name="mobile"
                        value={formData.mobile}
                        onChange={handleInputChange}
                        className={`connect-form__input ${formErrors.mobile ? 'connect-form__input--error' : ''}`}
                        placeholder="+27 12 345 6789"
                    />
                    {formErrors.mobile && <span className="connect-form__error">{formErrors.mobile}</span>}
                </div>

                <div className="connect-form__group">
                    <label className="connect-form__label">Message (Optional)</label>
                    <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        className="connect-form__textarea"
                        placeholder="Tell them a bit about what you're looking for..."
                        rows={3}
                    />
                </div>
            </form>
        </div>
    );

    const renderConfirmation = () => (
        <div className="connect-modal__content" key="confirmation">
            <div className="connect-confirmation">
                <div className="connect-confirmation__icon">✓</div>
                <h2 className="connect-confirmation__title">Request Sent!</h2>
                <p className="connect-confirmation__message">
                    {person?.name} will be in contact with you as soon as possible.
                    <br /><br />
                    {/* A confirmation has been sent to {formData.email}. */}
                </p>
                <button className="connect-modal__btn connect-modal__btn--primary" onClick={onClose}>
                    Done
                </button>
            </div>
        </div>
    );

    return (
        <div className="connect-modal-overlay" onClick={onClose}>
            <div className="connect-modal" onClick={e => e.stopPropagation()}>
                <button className="connect-modal__close" onClick={onClose}>×</button>

                {currentStep !== STEPS.CONFIRMATION && renderProgressIndicator()}

                {currentStep === STEPS.DETAILS && renderDetailsStep()}
                {currentStep === STEPS.CONFIRMATION && renderConfirmation()}

                {currentStep === STEPS.DETAILS && (
                    <div className="connect-modal__actions">
                        <button
                            className="connect-modal__btn connect-modal__btn--secondary"
                            onClick={onClose}
                        >
                            Cancel
                        </button>
                        <button
                            className="connect-modal__btn connect-modal__btn--primary"
                            onClick={handleSubmit}
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? 'Sending...' : 'Send Request'}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ConnectModal;
